import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, pcs, phrase, sizeOf, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; triad: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  C: { root: 'C2', triad: ['C4', 'E4', 'G4'] },
  G: { root: 'G1', triad: ['B3', 'D4', 'G4'] },
  Am: { root: 'A1', triad: ['C4', 'E4', 'A4'] },
  F: { root: 'F1', triad: ['C4', 'F4', 'A4'] },
  Em: { root: 'E2', triad: ['B3', 'E4', 'G4'] },
  Dm: { root: 'D2', triad: ['D4', 'F4', 'A4'] },
};

const PROGS = [
  ['C', 'G', 'Am', 'F'],
  ['F', 'G', 'Em', 'Am'],
  ['Am', 'F', 'C', 'G'],
  ['C', 'Am', 'F', 'G'],
  ['F', 'G', 'C', 'Am'],
] as const;

const HOOK: Note[][] = [
  [[0, 'E5', 2], [2, 'G5', 2], [4, 'C6', 3], [7, 'B5', 1, 0.75], [8, 'G5', 2], [10, 'E5', 2], [12, 'G5', 4]],
  [[0, 'D5', 2], [2, 'G5', 2], [4, 'B5', 3], [7, 'A5', 1, 0.75], [8, 'G5', 2], [10, 'D5', 2], [12, 'B4', 2], [14, 'D5', 2]],
  [[0, 'C5', 2], [2, 'E5', 2], [4, 'A5', 3], [7, 'G5', 1, 0.75], [8, 'E5', 2], [10, 'A5', 2], [12, 'C6', 4]],
  [[0, 'A5', 2], [2, 'G5', 1, 0.75], [3, 'F5', 1, 0.8], [4, 'A5', 2], [6, 'C6', 2], [8, 'B5', 4], [12, 'G5', 2], [14, 'D5', 2]],
];

const BASSES: readonly (readonly [step: number, semis: number, len: number][])[] = [
  [[0, 0, 2], [2, 12, 2], [4, 0, 2], [6, 12, 2], [8, 0, 2], [10, 12, 2], [12, 0, 2], [14, 12, 2]],
  [[0, 0, 2], [2, 0, 1], [3, 12, 1], [4, 0, 2], [6, 7, 2], [8, 0, 2], [10, 0, 1], [11, 12, 1], [12, 0, 2], [14, 7, 2]],
  [[0, 0, 1.5], [3, 0, 1], [4, 12, 1.5], [6, 0, 1.5], [8, 0, 1.5], [11, 0, 1], [12, 12, 1.5], [14, 7, 1.5]],
];

const ARPS = [
  [0, 1, 2, 3, 2, 1, 0, 1, 2, 3, 2, 1, 0, 1, 2, 3],
  [0, 2, 1, 3, 0, 2, 1, 3, 0, 2, 1, 3, 2, 3, 1, 2],
  [3, 2, 1, 0, 1, 2, 3, 2, 1, 0, 1, 2, 3, 1, 2, 0],
] as const;

const KEYS = [0, 2, -3, -1, 3, -2, 1] as const;
const POP_KICKS = ['X.....x.X.......', 'X.....x.X..x....', 'X.......X.x.....'] as const;
const DROP_KICKS = ['X...X...X...X...', 'X...X...X...X.x.'] as const;
const SNARES = ['....X.......X...', '....X.......X..o'] as const;
const HATS = ['x.o.x.o.x.o.x.o.', 'x.xox.o.x.xox.o.'] as const;
const FAST_HATS = ['xoxoxoxoxoxoxoxo', 'xoxoxoxox-xoxoxo'] as const;
const STABS = ['X.....X...X.....', '....X.....X.X...', 'X.....X.....X.X.', '..X...X...X...X.'] as const;
const HOT_STABS = ['X.x...X.x...X.x.', 'X...X.x...X.X.x.'] as const;
const MAJOR = [0, 2, 4, 5, 7, 9, 11] as const;

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 3 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(CHIP, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const bassRow = ch.pick(BASSES);
  const arpRow = ch.pick(ARPS);
  const popP = ch.pick(POP_KICKS);
  const dropP = ch.pick(DROP_KICKS);
  const snareP = ch.pick(SNARES);
  const hatP = ch.pick(HATS);
  const fastP = ch.pick(FAST_HATS);
  const stabP = ch.pick(STABS);
  const hotP = ch.pick(HOT_STABS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.triad]) }]),
        MAJOR,
        [69, 86],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 50, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const outro = total - form[form.length - 1]!.bars;
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const verse = find(form, 'A');
  const rng = createRng(`chip-${seed}`);
  const human = (v: number) => v * (0.94 + rng.float() * 0.12);

  const events: Ev[] = [];
  const taken = new Map<string, Ev>();
  const hit = (v: string, bar: number, step: number, vel: number) => {
    const key = `${v}:${String(bar)}:${String(step)}`;
    const old = taken.get(key);
    if (old !== undefined) {
      old.vel = Math.max(old.vel, vel);
      return;
    }
    const ev: Ev = { v, t: g.at(bar, step), bar, step, vel };
    taken.set(key, ev);
    events.push(ev);
  };
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes });

  const autos = [
    { v: 'padLp', points: [[g.at(0), 500], [g.at(intro), 1200], [g.at(total), 600]] },
    { v: 'arpLp', points: [[g.at(0), 1400], [g.at(intro), 5200], ...(brk === undefined ? [] : [[g.at(brk), 1800], [g.at(brk + sizeOf(form, 'break')), 5200]]), [g.at(outro), 4800], [g.at(total), 1200]] },
  ] as Score['autos'];

  play('coin', 0, 0, tr('B5'), s * 0.9, 0.8);
  play('coin', 0, 1, tr('E6'), s * 5, 0.8);

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const c = prog[i]!;
    const triad = c.triad.map(tr);
    const tones = [...triad, transpose(triad[0]!, 12)];
    const inDrop = b.name === 'drop' || b.name === 'drop2';
    const isA = b.name === 'A';
    const outroBody = b.name === 'outro' && !b.last;
    const body = isA || inDrop || outroBody;
    const root = tr(c.root);

    if (isA || outroBody) for (const h of hits(popP)) hit('kick', b.bar, h.step, h.vel);
    if (inDrop) for (const h of hits(dropP)) hit('kick', b.bar, h.step, h.vel);
    if (b.name === 'intro' && b.last && b.bars >= 4) for (const h of hits('X.......X.......')) hit('kick', b.bar, h.step, h.vel * 0.8);
    if (body) for (const h of hits(snareP)) hit('snare', b.bar, h.step, human(h.vel));
    if (inDrop && e >= 0.6) for (const h of hits('....x.......x...')) hit('clap', b.bar, h.step, human(h.vel));

    const hatRow = inDrop && e >= 0.55 ? fastP : hatP;
    if (body || b.name === 'break' || (b.name === 'intro' && b.index >= 1)) {
      const soft = b.name === 'intro' || b.name === 'break' ? 0.6 : 1;
      for (const h of hits(hatRow)) hit('hat', b.bar, h.step, human(h.vel * soft));
    }
    if (inDrop && (b.index % 2 === 1 || e >= 0.85)) hit('hatO', b.bar, 14, human(0.7));

    if (body) {
      for (const [step, semis, len] of bassRow) play('tri', b.bar, step, transpose(root, 12 + semis), len * s * 0.9, 0.95);
      play('sub', b.bar, 0, root, s * 7.6, 0.9);
      play('sub', b.bar, 8, root, s * 7.6, 0.85);
    }
    if (b.name === 'break') {
      play('tri', b.bar, 0, transpose(root, 12), s * 7.5, 0.9);
      play('tri', b.bar, 8, transpose(root, 19), s * 7.5, 0.85);
      play('sub', b.bar, 0, root, s * 15.2, 0.75);
    }
    if (b.name === 'intro' && b.index >= b.bars / 2) {
      for (const step of [0, 4, 8, 12]) play('tri', b.bar, step, transpose(root, 12), s * 1.6, 0.8);
    }

    if (b.name === 'intro' || isA || b.name === 'break' || b.name === 'outro') {
      const vel = b.name === 'intro' ? 0.45 : 0.55;
      arpRow.forEach((idx, step) => play('arp', b.bar, step, transpose(tones[idx]!, 12), s * 0.85, human(step % 4 === 0 ? vel : vel * 0.7)));
    }
    if (inDrop && e >= 0.65) {
      for (let k = 0; k < 32; k++) play('arp', b.bar, k / 2, transpose(tones[k % 4]!, 12), s * 0.42, human(k % 8 === 0 ? 0.42 : 0.28));
    }
    if (b.name === 'break' || b.name === 'intro') chord('pad', b.bar, 0, triad, g.barLength * 0.96, b.name === 'break' ? 0.8 : 0.55);

    const row = hook[i]!;
    if (isA && e >= 0.45 && b.index >= (b.bars >= 8 ? 4 : 0)) {
      for (const [step, note, len, vel] of row) play('leadSq', b.bar, step, tr(transpose(note, -12)), len * s * 0.9, human((vel ?? 0.9) * 0.8));
    }
    if (b.name === 'break' && b.index < b.bars - 1 && b.index >= b.bars / 2 - 1) {
      for (const [step, note, len, vel] of row) play('leadSq', b.bar, step, tr(note), len * s * 0.9, (vel ?? 0.9) * 0.7);
    }
    if (inDrop) {
      const harm = b.name === 'drop2' && e >= 0.75;
      const octave = b.name === 'drop2' && e >= 0.85;
      for (const [step, note, len, vel] of row) {
        const n = tr(note);
        play('lead', b.bar, step, n, len * s * 0.92, human(vel ?? 0.95));
        play('echo', b.bar, step + 3, n, len * s * 0.85, (vel ?? 0.9) * 0.32);
        if (harm) play('harm', b.bar, step, under(n, tones), len * s * 0.9, (vel ?? 0.9) * 0.5);
        if (octave) play('echo', b.bar, step, transpose(n, 12), len * s * 0.8, (vel ?? 0.9) * 0.3);
      }
    }

    const stabs = inDrop ? (b.name === 'drop2' && e >= 0.7 ? hotP : stabP) : isA && e >= 0.8 && b.index % 2 === 1 ? '..........X.....' : '';
    for (const h of hits(stabs)) chord('stab', b.bar, h.step, tones.slice(0, 3).map((n) => transpose(n, 12)), s * 1.2, h.vel);
    if (b.name === 'break' && !b.last) chord('stab', b.bar, 12, tones.slice(0, 3).map((n) => transpose(n, 12)), s * 1.2, 0.7);

    if (b.name === 'break' && b.last) {
      for (const h of hits('x.x.x.x.xxxxXXXX')) hit('snare', b.bar, h.step, 0.4 + 0.5 * (h.step / 16));
      const run = [...tones, ...tones.map((n) => transpose(n, 12))];
      for (let k = 0; k < 16; k++) play('arp', b.bar, 8 + k / 2, transpose(run[Math.min(run.length - 1, Math.floor(k / 2))]!, 12), s * 0.45, 0.35 + 0.35 * (k / 16));
    }
    if (isA && b.last) for (const h of hits('............x.xx')) hit('snare', b.bar, h.step, 0.7);
    if (b.name === 'intro' && b.last) for (const h of hits('........x.x.xxxx')) hit('snare', b.bar, h.step, 0.35 + 0.4 * (h.step / 16));
    if (b.name === 'outro' && b.last) chord('stab', b.bar, 0, tones.slice(0, 3).map((n) => transpose(n, 12)), s * 6, 0.8);
  }

  if (brk !== undefined) events.push({ v: 'rise', t: g.at(brk + sizeOf(form, 'break') / 2), end: g.at(brk + sizeOf(form, 'break')) - s * 2, bar: brk, step: 0, vel: 1 });
  for (const bar of [verse, drop1, drop2]) {
    if (bar === undefined) continue;
    hit('crash', bar, 0, 1);
    hit('impact', bar, 0, 1);
  }

  return {
    id: trackId(CHIP, seed, p),
    template: CHIP.id,
    seed,
    params: p,
    title: ch.orig ? CHIP.ref.title : titleOf(seed),
    genre: CHIP.genre,
    bpm: p.bpm,
    swing: 50,
    lufs: -11,
    key,
    form,
    mix: { reverbDecay: 1.6, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.26, drive: 1.5, softness: 1.25, masterLowpass: 12500, pumpDepth: 0.4, pumpRelease: 0.22 },
    events,
    autos,
    accents: { kick: 'kick', snare: 'snare', stab: 'stab', lead: 'melody', leadSq: 'melody', impact: 'drop' },
  };
}

function under(note: string, tones: readonly string[]): string {
  const pool = pcs(tones);
  for (let k = 3; k <= 9; k++) {
    const n = transpose(note, -k);
    if (pool.includes(pcs([n])[0]!)) return n;
  }
  return transpose(note, -12);
}

export const CHIP: Template = {
  id: 'chip',
  genre: 'soft chiptune',
  bpm: [112, 140],
  ref: { seed: 'ficha', title: 'Ficha', params: { bpm: 124, bars: 28, energy: 0.55 } },
  score,
};
